// W16 Resumen: the first screen of the day. Four numbers, what needs
// attention (with the action right there) and slot occupancy per phase.
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ChevronRight,
  CircleCheck,
  ClipboardList,
  MessageSquare,
  Package,
  ParkingSquare,
  SendHorizontal,
  ShieldAlert,
} from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useAvisos, useConfig, useInspecciones, useOrdenesAbiertas } from '@/datos/consultas';
import { fechaInspeccion, type Fase, type InspeccionDoc, type OrdenDoc } from '@/datos/tipos';
import { desdeRango, nombreTipo } from '@/inspecciones/filtros';
import { InsigniaEstado } from '@/inspecciones/InsigniaEstado';
import { permisos, useSesionActiva } from '@/lib/sesion';
import { fechaLarga, hora, inicioDelDia, useAhora } from '@/lib/tiempo';
import { calcularAtencion, type Atencion } from '@/resumen/atencion';
import { PrimerosPasos } from '@/resumen/PrimerosPasos';
import { Encabezado } from '@/shell/Encabezado';
import { useOrdenesVisibles } from '@/tablero/movimientos';
import { colaNeutral, estaAtrasada, ocupacionPorFase } from '@/tablero/logica';
import { useAccionesOrden } from '@/tablero/useAccionesOrden';
import { BarraCupo, ErrorCarga, Esqueleto, Tarjeta, TituloTarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { cn } from '@/ui/cn';
import { Contenedor } from '@/ui/pagina';
import { TextoPatente } from '@/ui/patente';

export const Route = createFileRoute('/_app/')({
  component: Resumen,
});

function Resumen() {
  const s = useSesionActiva();
  const ahora = useAhora();
  const hoy = inicioDelDia(ahora);
  const config = useConfig();
  const servidor = useOrdenesAbiertas();
  const ordenes = useOrdenesVisibles(servidor.data);
  // Same key as the inspections list "7 días": shared listener and cache.
  const inspQ = useInspecciones(desdeRango('7', hoy));
  // Latest notices: tells which signed PDFs were already sent (EnviarPdf).
  const avisosQ = useAvisos(null);
  const fases = useMemo(() => config.data?.fases ?? [], [config.data]);
  const acciones = useAccionesOrden(ordenes, fases);

  const inspecciones = useMemo(() => inspQ.data ?? [], [inspQ.data]);
  const deHoy = useMemo(
    () =>
      inspecciones
        .filter((i) => (fechaInspeccion(i)?.getTime() ?? 0) >= hoy)
        .sort(
          (a, b) => (fechaInspeccion(b)?.getTime() ?? 0) - (fechaInspeccion(a)?.getTime() ?? 0),
        ),
    [inspecciones, hoy],
  );
  const atencion = useMemo(
    () =>
      ordenes && fases.length
        ? calcularAtencion(ordenes, inspecciones, fases, ahora, avisosQ.data ?? null)
        : [],
    [ordenes, inspecciones, fases, ahora, avisosQ.data],
  );

  const cargando = !ordenes || !config.data;
  const enTaller = ordenes?.length ?? 0;
  const atrasados = ordenes?.filter((o) => estaAtrasada(o, fases, ahora)).length ?? 0;
  const ultima = fases.length - 1;
  const listos = ordenes?.filter((o) => !o.enZonaNeutral && o.faseIdx === ultima).length ?? 0;
  const ingresosHoy = deHoy.filter((i) => i.tipo === 'recepcion' && i.estado !== 'anulada').length;
  const nombre = s.nombre.split(' ')[0];

  return (
    <>
      <Encabezado
        titulo="Resumen"
        ayuda="resumen"
        subtitulo={`${fechaLarga(new Date(ahora)).replace(/^./, (c) => c.toUpperCase())} · Hola, ${nombre}`}
      />
      <Contenedor>
        {servidor.isError ? (
          <ErrorCarga error={servidor.error} />
        ) : (
          <>
            <PrimerosPasos config={config.data} ordenes={ordenes} inspecciones={inspQ.data} />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
              <Cifra
                titulo="En taller"
                valor={enTaller}
                detalle={`${colaNeutral(ordenes ?? []).length} en zona neutral`}
                cargando={cargando}
                a={{ to: '/tablero' }}
              />
              <Cifra
                titulo="Ingresos hoy"
                valor={ingresosHoy}
                detalle={
                  inspQ.data
                    ? `${deHoy.length} ${deHoy.length === 1 ? 'inspección' : 'inspecciones'} en total`
                    : ' '
                }
                cargando={!inspQ.data}
                a={{ to: '/inspecciones', search: { rango: 'hoy', tipo: 'recepcion' } }}
              />
              <Cifra
                titulo="Listos para entregar"
                valor={listos}
                detalle={`En ${fases[ultima]?.nombre ?? 'Entrega'}`}
                cargando={cargando}
                a={{ to: '/tablero', search: { fase: ultima } }}
              />
              <Cifra
                titulo="Atrasados"
                valor={atrasados}
                detalle={atrasados ? 'Más días de lo normal en su fase' : 'Todo dentro de plazo'}
                cargando={cargando}
                alerta={atrasados > 0}
                a={{ to: '/tablero', search: { atrasados: true } }}
              />
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
              <Tarjeta className="p-5 sm:p-6">
                <TituloTarjeta
                  accion={
                    atencion.length > 0 ? (
                      <span className="tabular text-[13px] text-text-2">{atencion.length}</span>
                    ) : undefined
                  }
                >
                  Requieren atención
                </TituloTarjeta>
                {cargando ? (
                  <div className="mt-4 flex flex-col gap-2.5">
                    {Array.from({ length: 4 }, (_, i) => (
                      <Esqueleto key={i} className="h-14" />
                    ))}
                  </div>
                ) : atencion.length === 0 ? (
                  <Vacio compacto icono={<CircleCheck />} titulo="Nada pendiente por ahora">
                    Sin atrasos, repuestos vencidos ni inspecciones con problemas.
                  </Vacio>
                ) : (
                  <ul className="mt-3 -mb-2 divide-y divide-border">
                    {atencion.slice(0, 12).map((a) => (
                      <FilaAtencion
                        key={a.clave}
                        a={a}
                        puedeAvisar={permisos.avisar(s.rol)}
                        onAvisar={acciones.pedirAviso}
                      />
                    ))}
                  </ul>
                )}
                {atencion.length > 12 && (
                  <p className="mt-3 text-[13px] text-text-2">
                    Y {atencion.length - 12} más. Revisa el tablero con el filtro de atrasados.
                  </p>
                )}
              </Tarjeta>

              <div className="flex min-w-0 flex-col gap-5">
                <Tarjeta className="p-5 sm:p-6">
                  <TituloTarjeta
                    accion={
                      <Link
                        to="/tablero"
                        className="text-[13px] font-bold text-primary-text hover:underline"
                      >
                        Ver tablero
                      </Link>
                    }
                  >
                    Ocupación por fase
                  </TituloTarjeta>
                  {cargando ? (
                    <Esqueleto className="mt-4 h-64" />
                  ) : (
                    <Ocupacion ordenes={ordenes} fases={fases} />
                  )}
                </Tarjeta>
                <Tarjeta className="p-5 sm:p-6">
                  <TituloTarjeta
                    accion={
                      <Link
                        to="/inspecciones"
                        search={{ rango: 'hoy' }}
                        className="text-[13px] font-bold text-primary-text hover:underline"
                      >
                        Ver todas
                      </Link>
                    }
                  >
                    Inspecciones de hoy
                  </TituloTarjeta>
                  {!inspQ.data ? (
                    <Esqueleto className="mt-4 h-32" />
                  ) : deHoy.length === 0 ? (
                    <Vacio compacto icono={<ClipboardList />} titulo="Aún no hay inspecciones hoy">
                      Aparecen aquí apenas la tablet las sincroniza.
                    </Vacio>
                  ) : (
                    <ul className="mt-2 -mb-2 divide-y divide-border">
                      {deHoy.slice(0, 8).map((i) => (
                        <FilaInspeccion key={i.id} i={i} />
                      ))}
                    </ul>
                  )}
                </Tarjeta>
              </div>
            </div>
          </>
        )}
      </Contenedor>
      {acciones.dialogos}
    </>
  );
}

type Destino =
  | { to: '/tablero'; search?: { fase?: number; atrasados?: boolean } }
  | { to: '/inspecciones'; search: { rango: 'hoy'; tipo?: 'recepcion' } };

function Cifra({
  titulo,
  valor,
  detalle,
  cargando,
  alerta,
  a,
}: {
  titulo: string;
  valor: number;
  detalle: string;
  cargando: boolean;
  alerta?: boolean;
  a: Destino;
}) {
  return (
    <Link
      {...(a as { to: '/tablero' })}
      className={cn(
        'group flex flex-col gap-1 rounded-tarjeta border bg-surface p-4 shadow-tarjeta transition-[border-color,box-shadow] duration-150 hover:border-border-input sm:p-5',
        alerta ? 'border-atraso-borde' : 'border-border',
      )}
    >
      <span className="flex items-center gap-1.5 text-[13px] font-bold text-text-2 sm:text-[14px]">
        {alerta && <AlertTriangle className="size-4 text-atraso-texto" aria-hidden="true" />}
        {titulo}
        <ChevronRight
          className="ml-auto size-4 text-na-strong opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden="true"
        />
      </span>
      {cargando ? (
        <Esqueleto className="my-1 h-9 w-16" />
      ) : (
        <span
          className={cn(
            'titulo tabular text-[34px] leading-[1.15]',
            alerta ? 'text-atraso-texto' : 'text-text',
          )}
        >
          {valor}
        </span>
      )}
      <span className="truncate text-[13px] text-text-2">{detalle}</span>
    </Link>
  );
}

const iconoAtencion: Record<Atencion['tipo'], ReactNode> = {
  atraso: <AlertTriangle />,
  repuesto: <Package />,
  cola: <ParkingSquare />,
  firma_rechazada: <ShieldAlert />,
  sin_pdf: <ClipboardList />,
  pdf_sin_enviar: <SendHorizontal />,
  observada: <MessageSquare />,
  borrador: <ClipboardList />,
};

function FilaAtencion({
  a,
  puedeAvisar,
  onAvisar,
}: {
  a: Atencion;
  puedeAvisar: boolean;
  onAvisar: (o: OrdenDoc) => void;
}) {
  const tono =
    a.gravedad === 3
      ? 'bg-que-soft text-que-texto'
      : a.gravedad === 2
        ? 'bg-ray-soft text-atraso-texto'
        : 'bg-surface-muted text-text-2';
  const aviso =
    puedeAvisar && a.orden && (a.tipo === 'atraso' || a.tipo === 'repuesto' || a.tipo === 'cola');
  return (
    <li className="flex items-center gap-3 py-3 sm:gap-4">
      <span
        className={cn(
          'grid size-9 flex-none place-items-center rounded-[10px] [&_svg]:size-[18px]',
          tono,
        )}
        aria-hidden="true"
      >
        {iconoAtencion[a.tipo]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2.5">
          <TextoPatente patente={a.patente} className="text-[17px]" />
          <span className="text-[14px] font-bold">{a.titulo}</span>
        </p>
        <p className="truncate text-[13px] text-text-2">{a.detalle}</p>
      </div>
      {aviso && a.orden ? (
        <Boton tamano="sm" onClick={() => a.orden && onAvisar(a.orden)}>
          Avisar
        </Boton>
      ) : a.inspeccionId ? (
        <Boton tamano="sm" variante="fantasma" asChild>
          <Link to="/inspecciones/$inspeccionId" params={{ inspeccionId: a.inspeccionId }}>
            Ver
          </Link>
        </Boton>
      ) : (
        <Boton tamano="sm" variante="fantasma" asChild>
          <Link to="/vehiculos/$patente" params={{ patente: a.patente }}>
            Ver
          </Link>
        </Boton>
      )}
    </li>
  );
}

function Ocupacion({ ordenes, fases }: { ordenes: OrdenDoc[] | undefined; fases: Fase[] }) {
  const ocupacion = ocupacionPorFase(ordenes ?? [], fases.length);
  const cola = colaNeutral(ordenes ?? []);
  return (
    <ul className="mt-4 flex flex-col gap-2.5">
      {fases.map((f, i) => {
        const n = ocupacion[i] ?? 0;
        const esperan = cola.filter((o) => (o.faseDestino ?? o.faseIdx) === i).length;
        return (
          <li key={f.id}>
            <Link
              to="/tablero"
              search={{ fase: i }}
              className="grid grid-cols-[minmax(0,140px)_1fr_auto] items-center gap-3 rounded-[8px] text-[14px] hover:text-primary-text"
            >
              <span className="truncate">{f.nombre}</span>
              <BarraCupo usados={n} total={f.cupo} grosor={6} />
              <span
                className={cn(
                  'tabular w-12 text-right text-[13px]',
                  n >= f.cupo ? 'font-bold text-atraso-texto' : 'text-text-2',
                )}
              >
                {n}/{f.cupo}
                {esperan > 0 && <span className="sr-only">, {esperan} esperando cupo</span>}
              </span>
            </Link>
          </li>
        );
      })}
      {cola.length > 0 && (
        <li className="pt-1 text-[13px] text-text-2">
          <ParkingSquare className="mr-1.5 inline size-4 align-[-3px]" aria-hidden="true" />
          {cola.length === 1 ? '1 auto espera cupo' : `${cola.length} autos esperan cupo`} en la
          zona neutral
        </li>
      )}
    </ul>
  );
}

function FilaInspeccion({ i }: { i: InspeccionDoc }) {
  const f = fechaInspeccion(i);
  return (
    <li>
      <Link
        to="/inspecciones/$inspeccionId"
        params={{ inspeccionId: i.id }}
        className="flex items-center gap-3 py-2.5 hover:text-primary-text"
      >
        <span className="tabular w-11 text-[13px] text-text-2">{f ? hora(f) : '—'}</span>
        <TextoPatente patente={i.patente} className="text-[16px]" />
        <span className="min-w-0 flex-1 truncate text-[13px] text-text-2">
          {nombreTipo[i.tipo]} · {i.vehiculo.marca} {i.vehiculo.modelo}
        </span>
        <InsigniaEstado inspeccion={i} />
      </Link>
    </li>
  );
}
